import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api/client";
import { ArrowLeft, Building2, Wrench, Ticket, ShieldCheck, Plus, X } from "lucide-react";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { fmtDate, fmtDateTime } from "@/lib/constants";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [addingEq, setAddingEq] = useState(false);

  const load = async () => {
    try { const r = await api.get(`/customers/${id}`); setData(r.data); }
    catch (e) { setErr(formatApiError(e)); }
  };
   
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  const { customer, equipment, tickets, amcs } = data;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to customers
      </button>

      <header className="mb-6 flex items-start gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Building2 className="h-7 w-7" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{customer.company_name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {[customer.contact_person, customer.mobile, customer.email].filter(Boolean).join(" · ") || "—"}
          </p>
        </div>
      </header>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      <section className="grid gap-4 lg:grid-cols-3">
        <div className="sharp-card p-5 lg:col-span-1">
          <p className="label-eyebrow">Details</p>
          <dl className="mt-3 space-y-2 text-sm">
            <Row k="Company" v={customer.company_name} />
            <Row k="Contact" v={customer.contact_person} />
            <Row k="Mobile" v={customer.mobile} />
            <Row k="Email" v={customer.email} />
            <Row k="GSTIN" v={customer.gst} />
            <Row k="Address" v={[customer.address, customer.city, customer.state, customer.pincode].filter(Boolean).join(", ")} />
          </dl>
        </div>

        <div className="sharp-card p-5 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <p className="label-eyebrow flex items-center gap-2"><Wrench className="h-3.5 w-3.5" /> Equipment</p>
            <button data-testid="customer-add-equipment" onClick={() => setAddingEq(true)} className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs">
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>
          <div className="space-y-2">
            {equipment.map((e) => (
              <div key={e.id} className="rounded-md border border-border/60 px-3 py-2 text-sm">
                <p className="font-medium">{e.name}</p>
                <p className="text-xs text-muted-foreground">
                  {[e.brand, e.model, e.serial_number].filter(Boolean).join(" · ")}
                </p>
                <p className="mt-1 text-[11px] text-muted-foreground">Warranty: {fmtDate(e.warranty_expiry)}</p>
              </div>
            ))}
            {equipment.length === 0 && <p className="text-xs text-muted-foreground">No equipment yet.</p>}
          </div>
        </div>

        <div className="sharp-card p-5 lg:col-span-3">
          <p className="label-eyebrow flex items-center gap-2"><Ticket className="h-3.5 w-3.5" /> Recent tickets</p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border/60">
                <tr className="text-left">
                  <th className="py-2 pr-4 label-eyebrow">Ticket</th>
                  <th className="py-2 pr-4 label-eyebrow">Complaint</th>
                  <th className="py-2 pr-4 label-eyebrow">Priority</th>
                  <th className="py-2 pr-4 label-eyebrow">Status</th>
                  <th className="py-2 pr-4 label-eyebrow">Created</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => (
                  <tr key={t.id} onClick={() => navigate(`/app/tickets/${t.id}`)} className="cursor-pointer border-b border-border/40 hover:bg-secondary/40">
                    <td className="py-2 pr-4 font-mono text-xs">{t.ticket_no}</td>
                    <td className="py-2 pr-4 max-w-sm truncate">{t.complaint}</td>
                    <td className="py-2 pr-4"><PriorityBadge priority={t.priority} /></td>
                    <td className="py-2 pr-4"><StatusBadge status={t.status} /></td>
                    <td className="py-2 pr-4 text-xs text-muted-foreground">{fmtDateTime(t.created_at)}</td>
                  </tr>
                ))}
                {tickets.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-xs text-muted-foreground">No tickets yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="sharp-card p-5 lg:col-span-3">
          <p className="label-eyebrow flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> AMC contracts</p>
          <div className="mt-3 space-y-2">
            {amcs.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2 text-sm">
                <div>
                  <p className="font-medium">{a.contract_no || "AMC"}</p>
                  <p className="text-[11px] text-muted-foreground">{fmtDate(a.start_date)} → {fmtDate(a.end_date)}</p>
                </div>
                <div className="text-xs text-muted-foreground">Visits: {a.visits_used}/{a.visits_total}</div>
              </div>
            ))}
            {amcs.length === 0 && <p className="text-xs text-muted-foreground">No contracts.</p>}
          </div>
        </div>
      </section>

      {addingEq && <EquipmentModal customerId={id} onClose={() => setAddingEq(false)} onSaved={() => { setAddingEq(false); load(); }} />}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="label-eyebrow shrink-0">{k}</dt>
      <dd className="text-right">{v || "—"}</dd>
    </div>
  );
}

function EquipmentModal({ customerId, onClose, onSaved }) {
  const [form, setForm] = useState({ customer_id: customerId, name: "", brand: "", model: "", serial_number: "", installation_date: "", warranty_expiry: "", notes: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/equipment", form); onSaved(); }
    catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg sharp-card">
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">Add equipment</h3>
          <button onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5 sm:grid-cols-2">
          {[
            ["name", "Name *", true, true],
            ["brand", "Brand"], ["model", "Model"], ["serial_number", "Serial #"],
            ["installation_date", "Installation date", false, false, "date"],
            ["warranty_expiry", "Warranty expiry", false, false, "date"],
          ].map(([k, l, req, full, type]) => (
            <div key={k} className={full ? "sm:col-span-2" : ""}>
              <label className="label-eyebrow mb-1.5 block">{l}</label>
              <input required={!!req} type={type || "text"} value={form[k]} onChange={set(k)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
          ))}
          {err && <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : "Add equipment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
